import React, { useEffect, useMemo, useState } from "react";
import { kcFetch, API_BASE } from "../../lib/apiClient";
import { useAppSettings, resolveLogo } from "../../lib/AppSettingsContext";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// FoodFormPublicPage — /donations/food-registration
// Public (no auth) Food Contribution Registration form.
// Mobile-first, Cinzel headings + Montserrat body, Royal Gold accents.
// On success → branded thank-you screen.
// ============================================================
export default function FoodFormPublicPage() {
  const { settings: appSettings, loaded: appSettingsLoaded } = useAppSettings();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await kcFetch(`${API_BASE}/public/food-form/schema`);
        if (res.ok) setSchema(await res.json());
      } catch (_) { /* offline tolerant */ }
      setLoading(false);
    })();
  }, []);

  const fields = schema?.fields || {};
  const dependents = schema?.dependents || { label: "Dependents", options: [] };
  const avoidFoods = schema?.avoidFoods || { label: "Foods You Do Not Consume", options: [] };
  const customFields = Array.isArray(schema?.customFields) ? schema.customFields : [];

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleAvoid = (opt) => setForm(f => {
    const cur = new Set(f.avoidFoods || []);
    cur.has(opt) ? cur.delete(opt) : cur.add(opt);
    return { ...f, avoidFoods: Array.from(cur) };
  });
  const setCustom = (id, v) => setForm(f => ({ ...f, custom: { ...(f.custom || {}), [id]: v } }));
  const toggleCustomCheckbox = (id, opt) => setForm(f => {
    const arr = new Set((f.custom || {})[id] || []);
    arr.has(opt) ? arr.delete(opt) : arr.add(opt);
    return { ...f, custom: { ...(f.custom || {}), [id]: Array.from(arr) } };
  });

  const allRequiredFilled = useMemo(() => {
    if (!schema) return false;
    const req = (k) => fields[k]?.required && fields[k]?.enabled;
    if (req("firstName") && !(form.firstName || "").trim()) return false;
    if (req("lastName") && !(form.lastName || "").trim()) return false;
    if (req("address") && !(form.address || "").trim()) return false;
    if (req("phonePrimary") && !(form.phonePrimary || "").trim()) return false;
    if (req("phoneSecondary") && !(form.phoneSecondary || "").trim()) return false;
    for (const cf of customFields) {
      if (!cf.required) continue;
      const v = (form.custom || {})[cf.id];
      if (cf.type === "checkbox") { if (!Array.isArray(v) || v.length === 0) return false; }
      else if (!String(v || "").trim()) return false;
    }
    return true;
  }, [schema, form, fields, customFields]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!allRequiredFilled || submitting) return;
    setError(""); setSubmitting(true);
    try {
      const res = await kcFetch(`${API_BASE}/public/food-form/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: (form.firstName || "").trim(),
          lastName: (form.lastName || "").trim(),
          address: (form.address || "").trim(),
          phonePrimary: (form.phonePrimary || "").trim(),
          phoneSecondary: (form.phoneSecondary || "").trim(),
          dependents: form.dependents || "",
          avoidFoods: form.avoidFoods || [],
          custom: form.custom || {},
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Could not submit your form. Please try again.");
      }
      const j = await res.json();
      setSubmittedAt(j.submittedAt);
    } catch (e2) {
      setError(e2.message || "Could not submit your form.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="ffr-page" data-testid="food-form-loading"><style>{CSS}</style><div className="ffr-card"><div className="ffr-loading">Loading form…</div></div></div>;
  }

  if (submittedAt) {
    return (
      <div className="ffr-page">
        <style>{CSS}</style>
        <div className="ffr-card ffr-thank-you" data-testid="food-form-thanks">
          <img src={resolveLogo(appSettings.appLogo)} alt={appSettings.appName} className="ffr-logo" />
          <div className="ffr-title">Thank You</div>
          <div className="ffr-sub">{appSettings.appName} · KGC Compassion</div>
          <div className="ffr-thanks-body">
            <div className="ffr-tick" aria-hidden="true"><CustomIcon name="check" size={40} /></div>
            <h2>We've received your registration.</h2>
            <p>Our compassion team will be in touch shortly. May the Lord bless you and your household.</p>
            <p className="ffr-thanks-meta">{appSettings.appTagline}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ffr-page" data-testid="food-form-public-page">
      <style>{CSS}</style>
      <form className="ffr-card" onSubmit={submit}>
        <img src={resolveLogo(appSettings.appLogo)} alt={appSettings.appName} className="ffr-logo" />
        <div className="ffr-title" data-testid="food-form-title">{schema?.title || "KGC Compassion — Food Contribution Registration"}</div>
        {schema?.description && <div className="ffr-desc" data-testid="food-form-description">{schema.description}</div>}

        {fields.firstName?.enabled && (
          <div className="ffr-group">
            <label htmlFor="ffr-fn">{fields.firstName.label}{fields.firstName.required ? " *" : ""}</label>
            <input id="ffr-fn" data-testid="ffr-firstName" value={form.firstName || ""} onChange={e => set("firstName", e.target.value)} required={!!fields.firstName.required} autoComplete="given-name" />
          </div>
        )}
        {fields.lastName?.enabled && (
          <div className="ffr-group">
            <label htmlFor="ffr-ln">{fields.lastName.label}{fields.lastName.required ? " *" : ""}</label>
            <input id="ffr-ln" data-testid="ffr-lastName" value={form.lastName || ""} onChange={e => set("lastName", e.target.value)} required={!!fields.lastName.required} autoComplete="family-name" />
          </div>
        )}
        {fields.address?.enabled && (
          <div className="ffr-group">
            <label htmlFor="ffr-ad">{fields.address.label}{fields.address.required ? " *" : ""}</label>
            <input id="ffr-ad" data-testid="ffr-address" value={form.address || ""} onChange={e => set("address", e.target.value)} required={!!fields.address.required} autoComplete="street-address" />
          </div>
        )}
        {fields.phonePrimary?.enabled && (
          <div className="ffr-group">
            <label htmlFor="ffr-p1">{fields.phonePrimary.label}{fields.phonePrimary.required ? " *" : ""}</label>
            <input id="ffr-p1" data-testid="ffr-phonePrimary" type="tel" inputMode="tel" value={form.phonePrimary || ""} onChange={e => set("phonePrimary", e.target.value)} required={!!fields.phonePrimary.required} autoComplete="tel" />
          </div>
        )}
        {fields.phoneSecondary?.enabled && (
          <div className="ffr-group">
            <label htmlFor="ffr-p2">{fields.phoneSecondary.label}{fields.phoneSecondary.required ? " *" : ""}</label>
            <input id="ffr-p2" data-testid="ffr-phoneSecondary" type="tel" inputMode="tel" value={form.phoneSecondary || ""} onChange={e => set("phoneSecondary", e.target.value)} required={!!fields.phoneSecondary.required} autoComplete="tel" />
          </div>
        )}

        {Array.isArray(dependents.options) && dependents.options.length > 0 && (
          <div className="ffr-group">
            <div className="ffr-grp-label" data-testid="ffr-dependents-label">{dependents.label}</div>
            <div className="ffr-radio-grid">
              {dependents.options.map((opt, i) => (
                <label key={opt} className={`ffr-radio ${form.dependents === opt ? "is-selected" : ""}`} data-testid={`ffr-dependents-${i}`}>
                  <input type="radio" name="dependents" value={opt} checked={form.dependents === opt} onChange={() => set("dependents", opt)} />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {Array.isArray(avoidFoods.options) && avoidFoods.options.length > 0 && (
          <div className="ffr-group">
            <div className="ffr-grp-label" data-testid="ffr-avoid-label">{avoidFoods.label}</div>
            <div className="ffr-check-grid">
              {avoidFoods.options.map((opt, i) => {
                const checked = (form.avoidFoods || []).includes(opt);
                return (
                  <label key={opt} className={`ffr-check ${checked ? "is-selected" : ""}`} data-testid={`ffr-avoid-${i}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleAvoid(opt)} />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {customFields.map((cf) => (
          <div key={cf.id} className="ffr-group">
            <label className="ffr-grp-label" data-testid={`ffr-custom-label-${cf.id}`}>{cf.label}{cf.required ? " *" : ""}</label>
            {cf.type === "short_text" && (
              <input data-testid={`ffr-custom-${cf.id}`} value={(form.custom?.[cf.id]) || ""} onChange={e => setCustom(cf.id, e.target.value)} required={!!cf.required} />
            )}
            {cf.type === "long_text" && (
              <textarea data-testid={`ffr-custom-${cf.id}`} value={(form.custom?.[cf.id]) || ""} onChange={e => setCustom(cf.id, e.target.value)} required={!!cf.required} rows={3} />
            )}
            {cf.type === "radio" && Array.isArray(cf.options) && (
              <div className="ffr-radio-grid">
                {cf.options.map((opt, i) => (
                  <label key={opt} className={`ffr-radio ${(form.custom?.[cf.id]) === opt ? "is-selected" : ""}`} data-testid={`ffr-custom-${cf.id}-${i}`}>
                    <input type="radio" name={cf.id} value={opt} checked={(form.custom?.[cf.id]) === opt} onChange={() => setCustom(cf.id, opt)} />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            )}
            {cf.type === "checkbox" && Array.isArray(cf.options) && (
              <div className="ffr-check-grid">
                {cf.options.map((opt, i) => {
                  const checked = ((form.custom?.[cf.id]) || []).includes(opt);
                  return (
                    <label key={opt} className={`ffr-check ${checked ? "is-selected" : ""}`} data-testid={`ffr-custom-${cf.id}-${i}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleCustomCheckbox(cf.id, opt)} />
                      <span>{opt}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {error && <div className="ffr-error" data-testid="food-form-error">{error}</div>}

        <button type="submit" className="ffr-submit" data-testid="food-form-submit" disabled={!allRequiredFilled || submitting}>
          {submitting ? "Sending…" : "Submit Registration"}
        </button>

        <div className="ffr-foot">
          <span>{appSettings.appName}</span>
          <span aria-hidden="true">·</span>
          <span>{appSettings.appTagline}</span>
        </div>
      </form>
    </div>
  );
}

const CSS = `
.ffr-page { min-height: 100vh; background: var(--kc-surface); padding: 28px 18px 64px; font-family: 'Montserrat',sans-serif; color: var(--kc-text); }
.ffr-card { max-width: 640px; margin: 0 auto; background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent); border-radius: 16px; padding: 28px 24px 24px; box-shadow: 0 20px 50px rgba(0,0,0,0.45); }
.ffr-logo { display: block; width: 96px; height: 96px; object-fit: cover; margin: 0 auto 14px; border-radius: 14px; border: 2px solid color-mix(in srgb, var(--kc-accent) 40%, transparent); background: var(--kc-elevated); }
.ffr-title { font-family: 'Cinzel', serif; font-size: 20px; line-height: 1.25; font-weight: 700; color: var(--kc-accent); text-transform: uppercase; letter-spacing: 0.06em; text-align: center; margin-bottom: 8px; }
.ffr-desc { font-size: 13px; color: var(--kc-sub); text-align: center; margin-bottom: 22px; line-height: 1.5; }
.ffr-group { margin-bottom: 18px; }
.ffr-group label, .ffr-grp-label { display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--kc-sub); margin-bottom: 8px; }
.ffr-group input, .ffr-group textarea { width: 100%; box-sizing: border-box; background: var(--kc-elevated); color: var(--kc-text); border: 1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent); border-radius: 10px; padding: 12px 14px; font-family: 'Montserrat',sans-serif; font-size: 15px; }
.ffr-group input:focus, .ffr-group textarea:focus { outline: none; border-color: var(--kc-accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--kc-accent) 22%, transparent); }
.ffr-radio-grid, .ffr-check-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 8px; }
.ffr-radio, .ffr-check { display: flex; align-items: center; gap: 10px; padding: 11px 14px; border-radius: 10px; border: 1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent); cursor: pointer; user-select: none; background: var(--kc-elevated); color: var(--kc-text); transition: background 0.15s, border-color 0.15s; font-size: 14px; }
.ffr-radio:hover, .ffr-check:hover { background: color-mix(in srgb, var(--kc-elevated) 70%, var(--kc-accent) 10%); }
.ffr-radio.is-selected, .ffr-check.is-selected { background: color-mix(in srgb, var(--kc-accent) 14%, transparent); border-color: var(--kc-accent); color: var(--kc-text); }
.ffr-radio input, .ffr-check input { accent-color: var(--kc-accent); }
.ffr-error { background: rgba(255,107,107,0.10); border: 1px solid rgba(255,107,107,0.45); color: #ff9b9b; padding: 10px 14px; border-radius: 10px; font-size: 13px; margin-bottom: 14px; }
.ffr-submit { display: block; width: 100%; padding: 14px 18px; border: none; border-radius: 12px; background: var(--kc-accent); color: var(--kc-on-accent); font-family: 'Montserrat',sans-serif; font-size: 15px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer; transition: filter 0.15s, transform 0.1s; }
.ffr-submit:hover:not(:disabled) { filter: brightness(1.05); }
.ffr-submit:active:not(:disabled) { transform: translateY(1px); }
.ffr-submit:disabled { opacity: 0.55; cursor: not-allowed; }
.ffr-foot { margin-top: 22px; text-align: center; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--kc-meta); display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
.ffr-loading { padding: 60px 20px; text-align: center; font-family: 'Cinzel',serif; color: var(--kc-accent); letter-spacing: 0.08em; text-transform: uppercase; }
.ffr-thank-you { text-align: center; }
.ffr-sub { font-size: 11px; letter-spacing: 0.10em; text-transform: uppercase; color: var(--kc-sub); margin-bottom: 30px; }
.ffr-tick { width: 80px; height: 80px; margin: 4px auto 22px; border-radius: 50%; background: rgba(74,222,128,0.15); border: 2px solid #4ade80; color: #4ade80; display: flex; align-items: center; justify-content: center; font-size: 38px; font-weight: 700; }
.ffr-thanks-body h2 { font-family: 'Cinzel',serif; font-size: 22px; color: var(--kc-text); margin: 0 0 12px; font-weight: 700; letter-spacing: 0.06em; }
.ffr-thanks-body p { font-size: 14px; color: var(--kc-sub); line-height: 1.6; max-width: 440px; margin: 0 auto 14px; }
.ffr-thanks-meta { font-style: italic; color: var(--kc-accent) !important; font-size: 12px !important; letter-spacing: 0.06em; text-transform: uppercase; }
@media (max-width: 540px) {
  .ffr-card { padding: 20px 16px 18px; border-radius: 12px; }
  .ffr-title { font-size: 17px; letter-spacing: 0.04em; }
  .ffr-radio-grid, .ffr-check-grid { grid-template-columns: 1fr; }
}
`;
